/**
 * useHomeARHomeIntegrationsLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeARHomeIntegrations
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeARAlmadarHomeView as HomeARAlmadarHomeViewEntity, HomeARHomeIntegrationsEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type HomeARHomeIntegrationsState =
  | 'idle';

export type HomeARHomeIntegrationsEvent =
  | 'INIT';

type HomeARHomeIntegrationsSlot = 'main';
interface HomeARHomeIntegrationsInternalState {
  machineState: HomeARHomeIntegrationsState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeARHomeIntegrationsEvent | null;
  slotWriter: Partial<Record<HomeARHomeIntegrationsSlot, HomeARHomeIntegrationsEvent | null>>;
  loading: boolean;
  pendingEvent: HomeARHomeIntegrationsEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeARAlmadarHomeViewEntity>;
}

type HomeARHomeIntegrationsAction =
  | { type: 'EVENT_SUCCESS'; event: HomeARHomeIntegrationsEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeARHomeIntegrationsSlot[]; renderClear?: HomeARHomeIntegrationsSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeARHomeIntegrationsEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeARAlmadarHomeViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeARHomeIntegrationsReducer(
  state: HomeARHomeIntegrationsInternalState,
  action: HomeARHomeIntegrationsAction
): HomeARHomeIntegrationsInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeARHomeIntegrationsState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeARAlmadarHomeViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeARAlmadarHomeViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeARAlmadarHomeViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeARHomeIntegrationsInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface HomeARHomeIntegrationsConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  alignment: 'center' | 'start';
  columns: number;
  edgeBottom: 'none' | 'curve' | 'wave' | 'arc' | 'tilt' | 'step' | 'scallop';
  edgeColor: 'background' | 'muted' | 'surface' | 'gradient' | 'inverse';
  edgeTop: 'none' | 'curve' | 'wave' | 'arc' | 'tilt' | 'step' | 'scallop';
  entries: { icon: string; name: string[]; role: string[] }[];
  heading?: string;
  lede?: string;
  markContent: { bg: Array<Array<Array<string | string[]> | string | string[]> | string | string[]>; children: { children: { align: Array<string | string[]>; children: Array<{ align: Array<string | string[]>; className: Array<string | string[]>; color: string; content: string; type: string; variant: string } | { align: Array<string | string[]>; className: Array<string | string[]>; content: string; type: string; variant: string } | { items: Array<Array<string | { logo: string[]; name: string[] }> | string>; type: string; variant: Array<string | string[]> }>; direction: string; gap: string; type: string }[]; maxWidth: string; padding: string; size: string; type: string }[]; edgeBottom: string; edgeColor: string; edgeTop: string; fullWidth: boolean; paddingY: string; texture: string; type: string };
  markIcon: string;
  marqueeContent: { children: { children: Array<{ children: { align: string; children: Array<{ align: string; className: string; color: string; content: string; type: string; variant: string } | { align: string; className: string; content: string; type: string; variant: string; weight: string }>; direction: string; gap: string; type: string }[]; maxWidth: string; padding: string; size: string; type: string } | { children: { children: Array<{ align: string; "aria-hidden": boolean; children: Array<Array<string | { align: string; children: Array<{ children: Array<{ className: string; color: string; content: string[]; type: string; variant: string } | { content: string[]; type: string; variant: string; weight: string }>; direction: string; gap: string; type: string } | { children: { color: string; name: string[]; size: string; type: string }[]; className: string; type: string }>; className: string; direction: string; gap: string; type: string }> | string>; className: string; direction: string; gap: string; type: string } | { align: string; children: Array<Array<string | { align: string; children: Array<{ children: Array<{ className: string; color: string; content: string[]; type: string; variant: string } | { content: string[]; type: string; variant: string; weight: string }>; direction: string; gap: string; type: string } | { children: { color: string; name: string[]; size: string; type: string }[]; className: string; type: string }>; className: string; direction: string; gap: string; type: string }> | string>; direction: string; gap: string; type: string }>; className: string; type: string }[]; className: string; fullWidth: boolean; type: string }>; direction: string; gap: string; type: string }[]; fullWidth: boolean; paddingY: string; type: string };
  proofLook: 'testimonials' | 'portfolio' | 'logos' | 'marquee' | 'badges';
  surface: 'plain' | 'muted' | 'accent' | 'inverse';
  texture: boolean;
}

const DEFAULT_HOME_AR_HOME_INTEGRATIONS_CONFIG = {
  alignment: "center",
  columns: 3,
  edgeBottom: "none",
  edgeColor: "background",
  edgeTop: "none",
  entries: [{ icon: "credit-card", name: ["i18n/t", "std-almadar-home:integrations.stripe.name"], role: ["i18n/t", "std-almadar-home:integrations.stripe.line"] }, { icon: "landmark", name: ["i18n/t", "std-almadar-home:integrations.gocardless.name"], role: ["i18n/t", "std-almadar-home:integrations.gocardless.line"] }, { icon: "file-signature", name: ["i18n/t", "std-almadar-home:integrations.docusign.name"], role: ["i18n/t", "std-almadar-home:integrations.docusign.line"] }, { icon: "file-spreadsheet", name: ["i18n/t", "std-almadar-home:integrations.accounting.name"], role: ["i18n/t", "std-almadar-home:integrations.accounting.line"] }, { icon: "calendar", name: ["i18n/t", "std-almadar-home:integrations.gcal.name"], role: ["i18n/t", "std-almadar-home:integrations.gcal.line"] }, { icon: "hard-drive", name: ["i18n/t", "std-almadar-home:integrations.gdrive.name"], role: ["i18n/t", "std-almadar-home:integrations.gdrive.line"] }, { icon: "phone", name: ["i18n/t", "std-almadar-home:integrations.twilio.name"], role: ["i18n/t", "std-almadar-home:integrations.twilio.line"] }, { icon: "mail", name: ["i18n/t", "std-almadar-home:integrations.email.name"], role: ["i18n/t", "std-almadar-home:integrations.email.line"] }, { icon: "webhook", name: ["i18n/t", "std-almadar-home:integrations.webhooks.name"], role: ["i18n/t", "std-almadar-home:integrations.webhooks.line"] }, { icon: "bell", name: ["i18n/t", "std-almadar-home:integrations.push.name"], role: ["i18n/t", "std-almadar-home:integrations.push.line"] }, { icon: "key-round", name: ["i18n/t", "std-almadar-home:integrations.sso.name"], role: ["i18n/t", "std-almadar-home:integrations.sso.line"] }, { icon: "database", name: ["i18n/t", "std-almadar-home:integrations.sql.name"], role: ["i18n/t", "std-almadar-home:integrations.sql.line"] }, { icon: "archive", name: ["i18n/t", "std-almadar-home:integrations.storage.name"], role: ["i18n/t", "std-almadar-home:integrations.storage.line"] }, { icon: "layers", name: ["i18n/t", "std-almadar-home:integrations.queues.name"], role: ["i18n/t", "std-almadar-home:integrations.queues.line"] }, { icon: "zap", name: ["i18n/t", "std-almadar-home:integrations.redis.name"], role: ["i18n/t", "std-almadar-home:integrations.redis.line"] }, { icon: "git-pull-request", name: ["i18n/t", "std-almadar-home:integrations.github.name"], role: ["i18n/t", "std-almadar-home:integrations.github.line"] }, { icon: "container", name: ["i18n/t", "std-almadar-home:integrations.docker.name"], role: ["i18n/t", "std-almadar-home:integrations.docker.line"] }, { icon: "activity", name: ["i18n/t", "std-almadar-home:integrations.otel.name"], role: ["i18n/t", "std-almadar-home:integrations.otel.line"] }, { icon: "megaphone", name: ["i18n/t", "std-almadar-home:integrations.metaAds.name"], role: ["i18n/t", "std-almadar-home:integrations.metaAds.line"] }, { icon: "circle-play", name: ["i18n/t", "std-almadar-home:integrations.youtube.name"], role: ["i18n/t", "std-almadar-home:integrations.youtube.line"] }, { icon: "sparkles", name: ["i18n/t", "std-almadar-home:integrations.ai.name"], role: ["i18n/t", "std-almadar-home:integrations.ai.line"] }, { icon: "map", name: ["i18n/t", "std-almadar-home:integrations.maps.name"], role: ["i18n/t", "std-almadar-home:integrations.maps.line"] }, { icon: "calendar-plus", name: ["i18n/t", "std-almadar-home:integrations.ical.name"], role: ["i18n/t", "std-almadar-home:integrations.ical.line"] }, { icon: "plug", name: ["i18n/t", "std-almadar-home:integrations.restApi.name"], role: ["i18n/t", "std-almadar-home:integrations.restApi.line"] }, { icon: "printer", name: ["i18n/t", "std-almadar-home:integrations.print.name"], role: ["i18n/t", "std-almadar-home:integrations.print.line"] }],
  markContent: { bg: ["if", ["==", "@config.surface", "muted"], "muted", ["if", ["==", "@config.surface", "accent"], "gradient", ["if", ["==", "@config.surface", "inverse"], "inverse", "transparent"]]], children: [{ children: [{ align: ["if", ["==", "@config.alignment", "center"], "center", "start"], children: [{ align: ["if", ["==", "@config.alignment", "center"], "center", "left"], className: ["if", ["==", "@config.heading", ""], "hidden", "text-balance"], content: "@config.heading", type: "typography", variant: "h2" }, { align: ["if", ["==", "@config.alignment", "center"], "center", "left"], className: ["if", ["==", "@config.lede", ""], "hidden", "max-w-2xl text-pretty"], color: "muted", content: "@config.lede", type: "typography", variant: "body" }, { items: ["array/map", "@config.entries", ["fn", "item", { logo: ["object/get", "@item", "logo", ""], name: ["object/get", "@item", "name", ""] }]], type: "social-proof", variant: ["if", ["==", "@config.proofLook", "badges"], "badges", "logos"] }], direction: "vertical", gap: "lg", type: "stack" }], maxWidth: "xl", padding: "lg", size: "xl", type: "container" }], edgeBottom: "@config.edgeBottom", edgeColor: "@config.edgeColor", edgeTop: "@config.edgeTop", fullWidth: true, paddingY: "xl", texture: "@config.texture", type: "box" },
  markIcon: "building-2",
  marqueeContent: { children: [{ children: [{ children: [{ align: "center", children: [{ align: "center", className: "text-balance", content: "@config.heading", type: "typography", variant: "h2", weight: "bold" }, { align: "center", className: "max-w-2xl text-pretty", color: "muted", content: "@config.lede", type: "typography", variant: "large" }], direction: "vertical", gap: "sm", type: "stack" }], maxWidth: "xl", padding: "lg", size: "xl", type: "container" }, { children: [{ children: [{ align: "stretch", children: ["array/map", "@config.entries", ["fn", "item", { align: "start", children: [{ children: [{ color: "primary", name: ["object/get", "@item", "icon", "blocks"], size: "md", type: "icon" }], className: "w-10 h-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center", type: "box" }, { children: [{ content: ["object/get", "@item", "name", ""], type: "typography", variant: "body", weight: "semibold" }, { className: "whitespace-normal", color: "muted", content: ["object/get", "@item", "role", ""], type: "typography", variant: "small" }], direction: "vertical", gap: "xs", type: "stack" }], className: "shrink-0 w-80 mx-2 rounded-xl border border-border bg-card p-5 shadow-elevation-card", direction: "horizontal", gap: "md", type: "stack" }]], direction: "horizontal", gap: "none", type: "stack" }, { align: "stretch", "aria-hidden": true, children: ["array/map", "@config.entries", ["fn", "item", { align: "start", children: [{ children: [{ color: "primary", name: ["object/get", "@item", "icon", "blocks"], size: "md", type: "icon" }], className: "w-10 h-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center", type: "box" }, { children: [{ content: ["object/get", "@item", "name", ""], type: "typography", variant: "body", weight: "semibold" }, { className: "whitespace-normal", color: "muted", content: ["object/get", "@item", "role", ""], type: "typography", variant: "small" }], direction: "vertical", gap: "xs", type: "stack" }], className: "shrink-0 w-80 mx-2 rounded-xl border border-border bg-card p-5 shadow-elevation-card", direction: "horizontal", gap: "md", type: "stack" }]], className: "motion-reduce:hidden", direction: "horizontal", gap: "none", type: "stack" }], className: "proof-marquee-track animate-almadar-marquee [animation-duration:120s] [animation-direction:reverse] hover:[animation-play-state:paused] motion-reduce:animate-none motion-reduce:w-auto motion-reduce:flex-wrap motion-reduce:justify-center", type: "box" }], className: "proof-marquee py-1 motion-reduce:[mask-image:none]", fullWidth: true, type: "box" }], direction: "vertical", gap: "lg", type: "stack" }], fullWidth: true, paddingY: "none", type: "box" },
  proofLook: "marquee",
  surface: "plain",
  texture: false,
} as HomeARHomeIntegrationsConfig;

export interface HomeARHomeIntegrationsLogicReturn {
  state: HomeARHomeIntegrationsState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeARHomeIntegrationsEvent | null;
  slotWriter: Partial<Record<HomeARHomeIntegrationsSlot, HomeARHomeIntegrationsEvent | null>>;
  loading: boolean;
  pendingEvent: HomeARHomeIntegrationsEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeARAlmadarHomeViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeARHomeIntegrationsEventPayloadMap]: (payload?: HomeARHomeIntegrationsEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeARHomeIntegrationsConfig;
}

export function useHomeARHomeIntegrationsLogic(config?: Partial<HomeARHomeIntegrationsConfig>): HomeARHomeIntegrationsLogicReturn {
  const mergedConfig: HomeARHomeIntegrationsConfig = { ...DEFAULT_HOME_AR_HOME_INTEGRATIONS_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-ar-home-integrations', orbitalName: 'HomeAR', traitName: 'HomeARHomeIntegrations' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeARHomeIntegrationsReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['HomeARAlmadarHomeView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
        if (false) {

        } else {

          if (false) {

          } else {

            if (true) {

            } else {

            }

          }

        }

      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeARHomeIntegrationsSlot[]; clear: HomeARHomeIntegrationsSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeARHomeIntegrationsSlot[], clear: [] as HomeARHomeIntegrationsSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeARAlmadarHomeView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeARHomeIntegrationsEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'HomeARHomeIntegrations': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeARHomeIntegrationsEventPayloadMap]: (payload?: HomeARHomeIntegrationsEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
  };

  useUIEvents(enqueueEvent, 'HomeAR.HomeARHomeIntegrations', ['INIT'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeARHomeIntegrations', (): TraitStateSnapshot => ({
      traitName: 'HomeARHomeIntegrations',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  mergedConfig.heading = mergedConfig.heading ?? (__t("std-almadar-home:integrations.heading"));
  mergedConfig.lede = mergedConfig.lede ?? (__t("std-almadar-home:integrations.lede"));
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
